"use client";

import { ChartCard } from "@/components/ui/chart-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { cn } from "@/lib/utils";

export default function SearchTimelineSkeleton({ rounded = true }: { rounded?: boolean }) {
  // Hydration-safe pattern
  const { isTerminal, mounted } = useThemeConfig();

  const showTerminal = mounted && isTerminal;

  return (
    <ChartCard
      title="Release Timeline"
      subtitle="Visual timeline of app releases showing market activity over time"
      className={rounded ? '' : 'rounded-none'}
    >
      <div className="h-56 -mx-2 relative">
        {/* Horizontal axis line - terminal mode uses emerald */}
        <div className="absolute left-2 right-2 top-1/2 -translate-y-0.5">
          <div className={cn(
            "h-px",
            showTerminal ? "bg-emerald-500/30" : "bg-border/30"
          )} />
        </div>

        {/* Terminal mode: grid lines */}
        {showTerminal && (
          <div className="absolute inset-2 pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="absolute w-px h-full bg-emerald-500/10"
                style={{ left: `${(i + 1) * 20}%` }}
              />
            ))}
          </div>
        )}

        {/* Skeleton bubbles at various positions */}
        <div className="absolute left-[10%] top-1/2 -translate-x-1/2 -translate-y-1/2">
          <Skeleton className="h-8 w-8 rounded-lg shadow-sm" />
        </div>
        <div className="absolute left-[25%] top-[40%] -translate-x-1/2 -translate-y-1/2">
          <Skeleton className="h-10 w-10 rounded-lg shadow-sm" />
        </div>
        <div className="absolute left-[40%] top-[60%] -translate-x-1/2 -translate-y-1/2">
          <Skeleton className="h-6 w-6 rounded-lg shadow-sm" />
        </div>
        <div className="absolute left-[55%] top-[45%] -translate-x-1/2 -translate-y-1/2">
          <Skeleton className="h-12 w-12 rounded-lg shadow-sm" />
        </div>
        <div className="absolute left-[70%] top-[55%] -translate-x-1/2 -translate-y-1/2">
          <Skeleton className="h-7 w-7 rounded-lg shadow-sm" />
        </div>
        <div className="absolute left-[85%] top-1/2 -translate-x-1/2 -translate-y-1/2">
          <Skeleton className="h-9 w-9 rounded-lg shadow-sm" />
        </div>

        {/* Bottom axis labels */}
        <div className="absolute bottom-0 left-2 right-2 flex justify-between px-2 pb-2">
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-12" />
        </div>
      </div>
    </ChartCard>
  );
}
